import React, { useEffect, useState } from 'react';
import axiosConn from '../../axiosConn';
import { getHitLabApiMessage } from './hitLabUtils';
import HitLabListenResult from './HitLabListenResult';

export default function HitLabResultOperations({
  intakeId,
  authConfig,
  draftUpdatedAt,
  draftDecision,
}) {
  const [ops, setOps] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [blocker, setBlocker] = useState(''),
    [followUpAt, setFollowUpAt] = useState('');
  const [correctedMaterialsAt, setCorrectedMaterialsAt] = useState(''),
    [evidence, setEvidence] = useState('');
  const [reconcileState, setReconcileState] = useState('unknown'),
    [providerMessageId, setProviderMessageId] = useState('');
  useEffect(() => {
    let active = true;
    setOps(null);
    setError('');
    axiosConn
      .get(`hitlab/admin/intakes/${intakeId}/result-operations`, authConfig)
      .then((response) => {
        if (!response.data?.data?.serviceWindow)
          throw new Error('Invalid result operations response');
        if (active) setOps(response.data.data);
      })
      .catch((err) => {
        if (active)
          setError(getHitLabApiMessage(err, 'Result operations unavailable.'));
      });
    return () => {
      active = false;
    };
  }, [intakeId, authConfig, draftUpdatedAt]);
  const run = async (path, body) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const response = await axiosConn.post(
        `hitlab/admin/intakes/${intakeId}/${path}`,
        body,
        authConfig,
      );
      setOps((current) => ({ ...current, ...response.data.data }));
    } catch (err) {
      setError(
        getHitLabApiMessage(
          err,
          'Operation failed. Reload to inspect state before retrying.',
        ),
      );
    } finally {
      setBusy(false);
    }
  };
  const latest = ops?.releases?.slice(-1)[0];
  const lastAttempt = latest?.notificationAttempts?.slice(-1)[0];
  const when = (date) =>
    date ? new Date(date).toLocaleString() : 'Not recorded';
  return (
    <section
      className="hitlab-result-operations"
      aria-label="Result fulfillment"
    >
      <h3>Account delivery & notification</h3>
      <p>
        A draft is private to staff. Release publishes a saved version to this
        artist’s account. Email is a separate action.
      </p>
      {error && (
        <p role="alert" className="hitlab-alert hitlab-alert-error">
          {error}
        </p>
      )}
      {ops && (
        <>
          {ops.plus && (
            <div className="hitlab-alert">
              <strong>
                Plus advertising:{' '}
                {ops.plus.advertisingBlockedAt
                  ? 'Permanently blocked'
                  : ops.plus.status}
              </strong>
              <p>
                Usable materials received: {when(ops.plus.materialsAvailableAt)}{' '}
                · Activation due: {when(ops.plus.activationDueAt)}
              </p>
              <p>
                Advertising term: {when(ops.plus.startsAt)} to{' '}
                {when(ops.plus.endsAt)} · Refund initiation due:{' '}
                {when(ops.plus.refundInitiateBy)}
              </p>
              <p>
                Dashboard refund:{' '}
                {ops.plus.plusRefund?.reference || 'Not recorded'} ·{' '}
                {ops.plus.plusRefund?.status || 'No provider status'}
              </p>
              <p>
                Use the documented operator reconciliation command to record
                materials, block advertising and record the Dashboard refund.
                Basic result access stays available.
              </p>
            </div>
          )}
          <p>
            <strong>Reviewability:</strong> {ops.serviceWindow.state} ·{' '}
            <strong>72-hour deadline:</strong> {when(ops.serviceWindow.dueAt)}
          </p>
          <p>
            Clock starts {when(ops.serviceWindow.availableAt)}. Staff delay and
            backlog never reset it.
          </p>
          {ops.serviceWindow.blocker && (
            <p role="status">
              Blocker: {ops.serviceWindow.blocker}. Follow up:{' '}
              {when(ops.serviceWindow.followUpAt)}
            </p>
          )}
          {(draftDecision === 'hold_second_listen' ||
            ops.serviceWindow.interimHold) && (
            <p role="status">
              Second-listen hold: interim work only. Follow-up:{' '}
              {when(ops.serviceWindow.interimHold?.followUpAt)}. The 72-hour
              deadline is unchanged; a hold cannot be released or sent as a
              final result.
            </p>
          )}
          <details>
            <summary>
              Confirm playable link or document missing materials
            </summary>
            <label>
              Specific link / materials blocker (not backlog)
              <textarea
                value={blocker}
                onChange={(e) => setBlocker(e.target.value)}
                maxLength={1000}
              />
            </label>
            <label>
              Follow-up date for a blocker
              <input
                type="datetime-local"
                value={followUpAt}
                onChange={(e) => setFollowUpAt(e.target.value)}
              />
            </label>
            <label>
              Corrected materials received at (only after a documented blocker)
              <input
                type="datetime-local"
                value={correctedMaterialsAt}
                onChange={(e) => setCorrectedMaterialsAt(e.target.value)}
              />
            </label>
            <label>
              Correction / reconciliation evidence
              <textarea
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                maxLength={1000}
              />
            </label>
            <button
              disabled={busy}
              type="button"
              onClick={() =>
                run('reviewability', {
                  state: 'playable',
                  correctedMaterialsAt: correctedMaterialsAt
                    ? new Date(correctedMaterialsAt).toISOString()
                    : undefined,
                  evidence,
                })
              }
            >
              Confirm link is playable
            </button>
            <button
              disabled={busy || !blocker || !followUpAt}
              type="button"
              onClick={() =>
                run('reviewability', {
                  state: 'blocked',
                  blocker,
                  followUpAt: new Date(followUpAt).toISOString(),
                })
              }
            >
              Record blocker & follow-up
            </button>
          </details>
          <button
            className="hitlab-primary-btn"
            type="button"
            disabled={
              busy ||
              !draftUpdatedAt ||
              draftDecision === 'hold_second_listen' ||
              ops.serviceWindow.state !== 'playable'
            }
            onClick={() => run('listen-result/release', { draftUpdatedAt })}
          >
            Release saved result to artist
          </button>
          {latest && (
            <>
              <p>
                Released {when(latest.releasedAt)} · Version {latest.version}
              </p>
              <p>
                <strong>Email:</strong> {latest.notificationState}. Provider
                acceptance does not prove delivery or reading.
              </p>
              <button
                type="button"
                disabled={
                  busy ||
                  !['not_requested', 'failed_definite'].includes(
                    latest.notificationState,
                  ) ||
                  latest.notificationAttempts.length >= 3
                }
                onClick={() =>
                  run(`listen-result/${latest.version}/notify`, {})
                }
              >
                Send result-ready notification
              </button>
              {latest.notificationAttempts?.map((attempt) => (
                <p key={attempt.id}>
                  {attempt.state} · {when(attempt.startedAt)} · Correlation:{' '}
                  {attempt.id} · Provider:{' '}
                  {attempt.providerMessageId || 'unconfirmed'} · Recipient:{' '}
                  {attempt.recipientEmail || 'unavailable'} {attempt.reason}
                </p>
              ))}
              {['sending', 'unknown'].includes(latest.notificationState) && (
                <details>
                  <summary>
                    Reconcile uncertain notification — do not blindly retry
                  </summary>
                  <p>
                    Wait at least 15 minutes. Search Mailgun for the exact
                    correlation and recipient. Missing events do not establish
                    failure. Keep unknown unless provider evidence establishes
                    acceptance or a definite rejection. Maximum three attempts
                    per version.
                  </p>
                  <label>
                    Provider finding
                    <select
                      value={reconcileState}
                      onChange={(e) => setReconcileState(e.target.value)}
                    >
                      <option value="unknown">Still unknown — no retry</option>
                      <option value="accepted">Accepted by provider</option>
                      <option value="failed_definite">
                        Definite provider rejection
                      </option>
                    </select>
                  </label>
                  <label>
                    Provider message ID
                    <input
                      value={providerMessageId}
                      onChange={(e) => setProviderMessageId(e.target.value)}
                    />
                  </label>
                  <label>
                    Exact evidence (include correlation, event, time)
                    <textarea
                      value={evidence}
                      onChange={(e) => setEvidence(e.target.value)}
                      maxLength={1000}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(`listen-result/${latest.version}/reconcile`, {
                        attemptId: lastAttempt?.id,
                        state: reconcileState,
                        providerMessageId,
                        evidence,
                      })
                    }
                  >
                    Record reconciliation
                  </button>
                </details>
              )}
              <details>
                <summary>Preview the released artist version</summary>
                <div className="hitlab-paid-experience">
                  <HitLabListenResult result={latest} />
                </div>
              </details>
            </>
          )}
        </>
      )}
    </section>
  );
}
